import { useMemo } from 'react';
import { Address, erc20Abi, formatUnits } from 'viem';
import { useConnection, useReadContract, useSignTypedData } from 'wagmi';

import {
  BUY_TOKEN,
  DEFAULT_PROTOCOL,
  SELL_TOKEN,
  type SwapToken,
} from '@/app/components/LlamaSwap/constants';
import {
  buildSignedSwapSendTx,
  calcPriceImpact,
  calcRate,
  toRawAmount,
  toRawAmountBigInt,
  type NormalizedQuote,
  type SwapSendTx,
} from '@/app/components/LlamaSwap/helpers';
import { useSwapQuote, type QuoteErrorType } from '@/app/components/LlamaSwap/useSwapQuote';
import { useTokenPrices } from '@/app/components/LlamaSwap/useTokenPrices';
import { useDebounce } from '@/app/shared/hooks/useDebounce';

export type UseLlamaSwapParams = {
  amount: string;
  slippage: string;
  chain?: string;
  sellToken?: SwapToken;
  buyToken?: SwapToken;
  debounceMs?: number;
};

export const useLlamaSwap = ({
  amount,
  slippage,
  chain = 'ethereum',
  sellToken = SELL_TOKEN,
  buyToken = BUY_TOKEN,
  debounceMs = 400,
}: UseLlamaSwapParams) => {
  const { address, isConnected } = useConnection();
  const { mutateAsync: signTypedData } = useSignTypedData();

  const debouncedAmount = useDebounce(amount, debounceMs);
  const debouncedSlippage = useDebounce(slippage, debounceMs);

  const { data: buyTokenDecimalsOnChain } = useReadContract({
    address: buyToken.address as Address,
    abi: erc20Abi,
    functionName: 'decimals',
  });

  const buyTokenDecimals = buyTokenDecimalsOnChain ?? buyToken.decimals;
  const { data: tokenPrices, isLoading: isPricesLoading } = useTokenPrices([
    sellToken.address,
    buyToken.address,
  ]);

  const sellPrice = tokenPrices?.[sellToken.address.toLowerCase()];
  const buyPrice = tokenPrices?.[buyToken.address.toLowerCase()];
  const hasSellPrice = sellPrice != null && sellPrice > 0;
  const hasBuyPrice = buyPrice != null && buyPrice > 0;

  const rawSellAmount = useMemo(
    () => toRawAmount(debouncedAmount, sellToken.decimals),
    [debouncedAmount, sellToken.decimals],
  );

  const rawSellAmountBigInt = useMemo(
    () => toRawAmountBigInt(debouncedAmount, sellToken.decimals),
    [debouncedAmount, sellToken.decimals],
  );

  const {
    data: quoteData,
    isFetching: isQuoteFetching,
    isPending: isQuotePending,
    dataUpdatedAt,
  } = useSwapQuote({
    chain,
    from: String(sellToken.address),
    to: String(buyToken.address),
    amount: rawSellAmount,
    userAddress: address ?? '',
    slippage: debouncedSlippage,
  });

  const activeQuote: NormalizedQuote | null = quoteData.quote;
  const quoteError: QuoteErrorType = quoteData.error;

  const buyAmount = useMemo(() => {
    if (!activeQuote?.amountReturned) return '';
    try {
      return Number(formatUnits(BigInt(activeQuote.amountReturned), buyTokenDecimals)).toFixed(6);
    } catch {
      return '';
    }
  }, [activeQuote?.amountReturned, buyTokenDecimals]);

  const rate = useMemo(
    () => calcRate(debouncedAmount, buyAmount),
    [debouncedAmount, buyAmount],
  );

  const minReceived = useMemo(() => {
    if (!buyAmount || !slippage) return '';
    return (Number(buyAmount) * (1 - Number(slippage) / 100)).toFixed(6);
  }, [buyAmount, slippage]);

  const priceImpact = useMemo(() => {
    if (!debouncedAmount || !buyAmount || !activeQuote || !hasSellPrice || !hasBuyPrice) {
      return null;
    }
    return calcPriceImpact(debouncedAmount, sellPrice, buyAmount, buyPrice);
  }, [debouncedAmount, buyAmount, activeQuote, hasSellPrice, hasBuyPrice, sellPrice, buyPrice]);

  const sellUsd =
    debouncedAmount && hasSellPrice ? Number(debouncedAmount) * sellPrice : null;
  const buyUsd = buyAmount && hasBuyPrice ? Number(buyAmount) * buyPrice : null;
  const spenderAddress = activeQuote?.tokenApprovalAddress as Address | undefined;

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: sellToken.address as Address,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address && spenderAddress ? [address, spenderAddress] : undefined,
    query: { enabled: !!address && !!spenderAddress },
  });

  const needsApproval = allowance !== undefined && allowance < rawSellAmountBigInt;
  const isDebouncing = amount !== debouncedAmount || slippage !== debouncedSlippage;
  const isLoadingQuote =
    isDebouncing || ((!activeQuote || isQuotePending) && (isQuoteFetching || isPricesLoading));
  const hasAmount = !!debouncedAmount && Number(debouncedAmount) > 0;

  const showQuoteError =
    hasAmount &&
    !isLoadingQuote &&
    !isQuoteFetching &&
    !activeQuote &&
    quoteError !== null;

  const buildSwapSendTx = async (quote: NormalizedQuote = activeQuote!): Promise<SwapSendTx> => {
    if (!quote) throw new Error('Swap quote is not available');
    return buildSignedSwapSendTx(quote, signTypedData);
  };

  return {
    isConnected,
    address,
    sellToken,
    buyToken,
    providerLabel: DEFAULT_PROTOCOL,
    activeQuote,
    buyAmount,
    buyTokenDecimals,
    rate,
    minReceived,
    priceImpact,
    sellUsd,
    buyUsd,
    needsApproval,
    spenderAddress,
    refetchAllowance,
    isLoadingQuote,
    isQuoteFetching,
    dataUpdatedAt,
    showQuoteError,
    quoteError,
    buildSwapSendTx,
  };
};
